"use client";

import { useEffect, useRef } from "react";

import Link from "next/link";

import cn from "classnames";

import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";

import { brown } from "@/app/font";
import type { GetNavigationQueryResult } from "@/sanity.types";

import { HeaderProps } from "./header.component";
import { hasNavigationLink } from "./navigationQuery.types";

export const HeaderSecurityMenu = ({
  variant,
  menu,
}: HeaderSecurityMenuProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!detailsRef.current?.open) return;
      const target = event.target as Node | null;
      if (!target || detailsRef.current.contains(target)) return;
      detailsRef.current.removeAttribute("open");
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  if (variant === "minimal") return null;
  const renderNav = () => {
    if (!menu?.items?.length) return null;
    return menu.items.map((nav) => {
      if (!hasNavigationLink(nav) || !nav.link.url) return null;
      return (
        <li
          data-testid="header-security-menu-list-item"
          key={nav.label}
          className="hover:bg-secondary-100 flex rounded-3xl transition-all duration-600">
          <Link
            className={cn(
              brown.className,
              "w-full p-2 whitespace-nowrap normal-case xl:px-4 xl:text-[18px]",
            )}
            href={{
              pathname: nav.link.url,
              query: nav.link.queryString,
            }}
            target={nav.link.openInNewTab ? "_blank" : "_self"}>
            {nav.label}
          </Link>
        </li>
      );
    });
  };
  return (
    <details
      ref={detailsRef}
      name="header-details"
      data-testid="header-security-menu"
      className="group hidden min-w-0 shrink flex-col lg:flex">
      <summary
        data-testid="header-security-menu-summary"
        className={cn(
          brown.className,
          "flex min-w-0 cursor-pointer list-none gap-0.5 overflow-hidden [&::-webkit-details-marker]:hidden",
        )}>
        <div className="min-w-0 truncate overflow-hidden text-ellipsis whitespace-nowrap">
          {menu?.label}
        </div>
        <ChevronDownIcon
          data-testid="header-security-menu-chevron"
          className="shrink-0 group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <nav
        data-testid="header-security-menu-nav"
        className="border-secondary-100 absolute top-24 rounded-2xl border p-4"
        aria-label={`${menu?.label || "Security"} menu`}
        style={{
          background:
            "linear-gradient(90deg, rgba(0, 184, 241, 0.90) -17.8%, rgba(7, 64, 139, 0.90) 21.32%, var(--color-secondary, rgba(0, 34, 106, 0.90)) 75.95%, var(--color-primary-400, rgba(0, 184, 241, 0.90)) 119.27%)",
        }}>
        <ul
          data-testid="header-security-menu-list"
          className="flex flex-col gap-1">
          {renderNav()}
        </ul>
      </nav>
    </details>
  );
};

export type HeaderSecurityMenuProps = HeaderProps & {
  menu?: GetNavigationQueryResult;
};
